import { useTranslation } from 'react-i18next';
import { MAP_FONTS_TO_CHALK } from 'utils/constants';

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Text } from '@thinkific/chalk';
import { ChalkThemeProvider } from '@thinkific/chalk/react-providers';

import { MentionNodeAttrs } from '@tiptap/extension-mention';
import { SuggestionProps } from '@tiptap/suggestion';

import { useCommunityContext } from 'components/CommunityContext';
import UserAvatar from 'components/UserAvatar';

import * as css from './MentionList.css';

const MentionList = forwardRef(function MentionList(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { items, command }: SuggestionProps<any, MentionNodeAttrs>,
  ref
) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLUListElement>(null);
  const { community } = useCommunityContext();
  const selectItem = (index: number) => {
    command(items[index]);
    // document.getElementById('mentions-overlay')?.remove();
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  const upHandler = () => {
    setSelectedIndex((selectedIndex + items.length - 1) % items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <ChalkThemeProvider
      bodyFont={MAP_FONTS_TO_CHALK[community.font]}
      primaryColor={community.primaryColor}
      titleFont={MAP_FONTS_TO_CHALK[community.font]}
    >
      <div className={css.mentionListContainer}>
        {items.length > 0 ? (
          <ul
            aria-activedescendant={`mention-item-${selectedIndex}`}
            className={css.mentionList}
            ref={containerRef}
            role="menu"
            tabIndex={0}
            onMouseDown={(e) => e.preventDefault()} // prevent focus loss
          >
            {items.map((item, index) => (
              <li
                className={css.mentionListItem[index === selectedIndex ? 'selected' : 'unselected']}
                key={item.label}
              >
                <Text
                  className={css.mentionListItemButton}
                  id={`mention-item-${index}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectItem(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      selectItem(index);
                    }
                  }}
                >
                  <UserAvatar
                    avatarUrl={item.avatarUrl}
                    size="xs"
                    userId={item.id}
                    username={item.label}
                  />

                  {item.label}
                </Text>
              </li>
            ))}
          </ul>
        ) : (
          <Text
            as="span"
            color="onLight.text.secondary"
            dataQa="text-body-base-test-id"
            variant="body.base"
          >
            {t('communities.mentions.no_results_found', 'No mentionable users found')}
          </Text>
        )}
      </div>
    </ChalkThemeProvider>
  );
});

export default MentionList;
